<template>
  <div class="callback-container">
    <div v-if="isLoading" class="loading-section">
      <p>Processing login, please wait...</p>
      <!-- Basic spinner -->
      <div class="spinner"></div>
    </div>
    <div v-if="error" class="error-section">
      <h2>Login Failed</h2>
      <p class="error-message">{{ error }}</p>
      <router-link to="/auth" class="retry-link">Try Login Again</router-link>
    </div>
    <!-- Successful login will redirect, so no explicit success message needed here -->
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/authStore'; // Import the auth store
import { handleCasdoorCallback } from '@/services/api'; // Import the API service
// import { useCasdoor } from 'casdoor-vue-sdk'; // No longer using SDK's signin for backend call

/**
 * @file CasdoorCallback.vue
 * @description This component handles the OAuth callback from Casdoor.
 * It manually parses the authorization code and state from the URL,
 * then uses `fetch` to call the application's backend. The backend
 * (mocked in vue.config.js for development) is expected to exchange this code
 * with Casdoor for an ID token, verify it, and return an application-specific
 * session token and user information. This component stores the session token
 * and user data in localStorage and redirects the user upon success or failure.
 */

const router = useRouter();
const auth = useAuthStore(); // Get an instance of the auth store
// const { signin } = useCasdoor(); // SDK signin removed

/**
 * Reactive ref for tracking the loading state during the sign-in process.
 * @type {import('vue').Ref<boolean>}
 */
const isLoading = ref(true);
/**
 * Reactive ref for storing any error messages that occur during the sign-in process.
 * @type {import('vue').Ref<string|null>}
 */
const error = ref(null);

/**
 * Vue `onMounted` lifecycle hook.
 * This function is executed when the component is mounted. It initiates the sign-in
 * process by calling the Casdoor SDK's `signin` method.
 */
onMounted(async () => {
  isLoading.value = true;
  error.value = null;
  console.log('[CasdoorCallback] Mounted. Attempting manual sign in...');

  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state'); // Optional, but good to get if present

  console.log('[CasdoorCallback] Parsed from URL - code:', code, 'state:', state);

  if (!code) {
    console.error('[CasdoorCallback] Authorization code not found in URL.');
    error.value = 'Authorization code not found in URL.';
    isLoading.value = false;
    return;
  }

  // const backendApiUrl = 'http://localhost:8001/api/v1/auth/casdoor-callback'; // REMOVED
  console.log('[CasdoorCallback] Calling backend via handleCasdoorCallback with code:', code, 'state:', state);

  try {
    // const fetchResponse = await fetch(backendApiUrl, { ... }); // REMOVED
    // console.log('[CasdoorCallback] Fetch call completed. Status:', fetchResponse.status); // REMOVED
    // const responseData = await fetchResponse.json(); // REMOVED
    // console.log('[CasdoorCallback] Fetch response data:', responseData); // REMOVED

    // if (!fetchResponse.ok) { ... } // REMOVED - Error handling is now within handleCasdoorCallback

    const responseData = await handleCasdoorCallback(code, state); // NEW: Call the imported service function
    console.log('[CasdoorCallback] Backend call via handleCasdoorCallback completed. Response data:', responseData);

    // Process responseData (handleCasdoorCallback already returns .json() or throws error)
    // This logic remains largely the same, as responseData is the parsed JSON
    if (responseData && (responseData.success === true || responseData.status === 'ok' || responseData.token)) {
      const appToken = responseData.token; // Assuming mock returns token directly
      const userData = responseData.user;  // Assuming mock returns user directly

      if (!appToken) {
        console.error('[CasdoorCallback] Application token not found in backend response.');
        throw new Error('Application token not found in response from backend.');
      }

      // Use the auth store to log in
      auth.login(appToken, userData);
      // localStorage interaction is now handled by authStore.login()

      console.log('[CasdoorCallback] Login successful (via authStore). Token stored. Redirecting to home...');
      router.push('/');
    } else {
      const errorMessage = responseData.message || 'Login failed: Could not retrieve token or user data from backend (manual fetch).';
      console.error(`[CasdoorCallback] Signin failed: ${errorMessage}`, responseData);
      throw new Error(errorMessage);
    }
  } catch (err) {
    console.error('[CasdoorCallback] Error during handleCasdoorCallback process:', err);
    error.value = err.message || 'An unexpected error occurred during login.';
    // err.message should be sufficient as handleCasdoorCallback prepares it.
  } finally {
    isLoading.value = false;
    console.log('[CasdoorCallback] Manual signin process finished. isLoading set to false.');
  }
});
</script>

<style scoped>
.callback-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  text-align: center;
  font-family: Arial, sans-serif;
  padding: 20px;
}

.loading-section p {
  font-size: 1.2em;
  color: #333;
  margin-bottom: 20px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #007bff;
  animation: spin 1s ease infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-section h2 {
  color: #d9534f; /* Bootstrap's danger color */
  margin-bottom: 10px;
}

.error-message {
  color: #721c24; /* Darker red for text */
  background-color: #f8d7da; /* Light red background */
  border: 1px solid #f5c6cb;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  max-width: 400px;
  word-wrap: break-word;
}

.retry-link {
  display: inline-block;
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  text-decoration: none;
  border-radius: 5px;
  transition: background-color 0.3s ease;
}

.retry-link:hover {
  background-color: #0056b3;
}
</style>
